// OrlShellDeform — drives the eyes/teeth branch from the exact rig.
//
// Sibling of OrlDeform with the same pose()/runCpu()/download() surface, so
// OGSBranch consumes it unchanged. The difference is what it can drive:
// OrlDeform skins head_lod0, the ONE mesh a `head.dna` bakes. This drives the
// OTHER branch a multi-region bundle ships — a ~760-880 vert mesh that is not
// MetaHuman topology and differs per character, but which is the character's
// eyeballs and teeth rows, decimated. Without it that branch renders frozen
// while the head talks. shellDeform.js carries the measurements, the case for
// treating the shells as rigid, and the fidelity this does NOT claim.
//
// SPACE. Output is the rig's own, in centimetres, exactly like OrlDeform — so
// OGSBranch.calibrate() fits this branch's similarity and `corr` the same way it
// does for the head. That is exact, not approximate: a similarity conjugates a
// rigid motion, S(rel(p)) = (S∘rel∘S⁻¹)(S(p)).
//
// CPU only, deliberately: four 4x4 multiplies over ~800 vertices, where a WGSL
// pass would cost more in dispatch than it saves.

import { createOrlRig } from "../../../lib/orl/orlRig.js";
import {
  splitShells,
  shellCentroids,
  assignShellJoints,
  poseShells,
  SHELL_JOINT_NAMES,
} from "../../../lib/orl/shellDeform.js";
import type { DirtySource, FrameInputs, Node, NodeContext, NodeTensor, PortSpec } from "../types";

export interface OrlShellDeformOptions {
  id: string;
  getBytes: (name: string) => Uint8Array | undefined;
  controlNames: string[];
  /** This branch's baked neutral vertices, in the bundle's own space. */
  neutralVertices: Float32Array;
  /** This branch's triangle indices — the shell split is by connectivity. */
  faces: Uint32Array | Uint16Array;
  rigInputPort?: string;
  outputPort?: string;
}

type OrlRig = Awaited<ReturnType<typeof createOrlRig>>;

/** Row-major 4x4 affine inverse (rotation+scale+translation; no projection). */
function invertAffine(m: Float64Array, off: number, out: Float64Array, oo: number): boolean {
  const a = m[off], b = m[off + 1], c = m[off + 2];
  const d = m[off + 4], e = m[off + 5], f = m[off + 6];
  const g = m[off + 8], h = m[off + 9], i = m[off + 10];
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (!Number.isFinite(det) || Math.abs(det) < 1e-12) return false;
  const s = 1 / det;
  const i00 = (e * i - f * h) * s, i01 = (c * h - b * i) * s, i02 = (b * f - c * e) * s;
  const i10 = (f * g - d * i) * s, i11 = (a * i - c * g) * s, i12 = (c * d - a * f) * s;
  const i20 = (d * h - e * g) * s, i21 = (b * g - a * h) * s, i22 = (a * e - b * d) * s;
  const tx = m[off + 3], ty = m[off + 7], tz = m[off + 11];
  out[oo] = i00; out[oo + 1] = i01; out[oo + 2] = i02;
  out[oo + 3] = -(i00 * tx + i01 * ty + i02 * tz);
  out[oo + 4] = i10; out[oo + 5] = i11; out[oo + 6] = i12;
  out[oo + 7] = -(i10 * tx + i11 * ty + i12 * tz);
  out[oo + 8] = i20; out[oo + 9] = i21; out[oo + 10] = i22;
  out[oo + 11] = -(i20 * tx + i21 * ty + i22 * tz);
  out[oo + 12] = 0; out[oo + 13] = 0; out[oo + 14] = 0; out[oo + 15] = 1;
  return true;
}

/** rel = world · restInv, row-major, written into `out` at joint j. */
function mulAffine(world: Float64Array, wo: number, inv: Float64Array, io: number, out: Float32Array, oo: number): void {
  for (let r = 0; r < 3; r++) {
    const a0 = world[wo + r * 4], a1 = world[wo + r * 4 + 1], a2 = world[wo + r * 4 + 2], a3 = world[wo + r * 4 + 3];
    out[oo + r * 4]     = a0 * inv[io] + a1 * inv[io + 4] + a2 * inv[io + 8] + a3 * inv[io + 12];
    out[oo + r * 4 + 1] = a0 * inv[io + 1] + a1 * inv[io + 5] + a2 * inv[io + 9] + a3 * inv[io + 13];
    out[oo + r * 4 + 2] = a0 * inv[io + 2] + a1 * inv[io + 6] + a2 * inv[io + 10] + a3 * inv[io + 14];
    out[oo + r * 4 + 3] = a0 * inv[io + 3] + a1 * inv[io + 7] + a2 * inv[io + 11] + a3 * inv[io + 15];
  }
  out[oo + 12] = 0; out[oo + 13] = 0; out[oo + 14] = 0; out[oo + 15] = 1;
}

export class OrlShellDeform implements Node {
  readonly id: string;
  readonly type = "orlShellDeform";
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[];
  readonly bindings = [];

  private readonly rigPort: string;
  private readonly outPort: string;
  private readonly getBytes: (name: string) => Uint8Array | undefined;
  private readonly controlNames: string[];
  private readonly neutralBundle: Float32Array;
  private readonly faces: Uint32Array | Uint16Array;

  private rig: OrlRig | null = null;
  private dims: number[] = [];
  private shells: Int32Array[] = [];
  private jointOfShell: number[] = [];
  private candidates: number[] = [];
  /** This branch's neutral, mapped into rig space — what we actually pose. */
  private neutralRig: Float32Array | null = null;
  private restInv: Float64Array | null = null;
  private rel: Float32Array | null = null;
  private scratch: Float32Array | null = null;
  private ready = false;

  constructor(opts: OrlShellDeformOptions) {
    this.id = opts.id;
    this.getBytes = opts.getBytes;
    this.controlNames = opts.controlNames;
    this.neutralBundle = opts.neutralVertices;
    this.faces = opts.faces;
    this.rigPort = opts.rigInputPort ?? "rig";
    this.outPort = opts.outputPort ?? "verts";
    this.inputs = [{ name: this.rigPort }];
    this.outputs = [{ name: this.outPort }];
    this.dims = [1, this.neutralBundle.length / 3, 3];
  }

  async init(_ctx: NodeContext): Promise<void> {
    // expectVerts is the HEAD's vertex count and would reject this branch, so it
    // is deliberately not passed: this deform never returns the rig's own mesh.
    this.rig = await createOrlRig({
      getBytes: this.getBytes,
      controlNames: this.controlNames,
      minMatched: Math.floor(this.controlNames.length * 0.8),
      log: (m: string) => console.log(`[ogs] ${this.id}: ${m}`),
    });
    const names: string[] | undefined = (this.rig.manifest as { jointNames?: string[] }).jointNames;
    if (!names) {
      // Loud and specific: an older pack is fine for the head and simply cannot
      // drive this branch. Naming the fix beats a branch that silently freezes.
      console.warn(`[ogs] ${this.id}: this orl_pack.bin carries no jointNames — `
        + `re-bake it (tools/bake_orl_pack.mjs) to drive the eyes/teeth branch; staying at neutral`);
      return;
    }
    this.candidates = SHELL_JOINT_NAMES.map((n) => names.indexOf(n));
    const missing = SHELL_JOINT_NAMES.filter((_, k) => this.candidates[k] < 0);
    if (missing.length === SHELL_JOINT_NAMES.length) {
      console.warn(`[ogs] ${this.id}: none of the eye/teeth joints exist in this DNA — staying at neutral`);
      return;
    }
    if (missing.length) console.warn(`[ogs] ${this.id}: DNA has no ${missing.join(", ")} — those shells stay at neutral`);
    this.shells = splitShells(this.faces, this.neutralBundle.length / 3);
  }

  /** No GPU path — see the header. Present so the node is interface-compatible
   *  with OrlDeform, which OGSBranch calls this on unconditionally. */
  async initGpu(_device: GPUDevice | null): Promise<void> {}

  /** Place this branch's shells in RIG space, using the frame a sibling branch
   *  already fitted (the head's `calibrate()`).
   *
   *  This branch cannot fit that frame itself: its mesh is not in the DNA, so
   *  there is no correspondence to fit against. The head's frame is the right
   *  one to borrow because both branches are baked in a single bundle space —
   *  measured, the borrowed frame lands these shells 0.11-0.35 cm from the DNA's
   *  own eye and teeth meshes.
   *
   *  Must run before calibrate(). Returns false when the branch stays neutral. */
  adoptRigSpace(lin: Float32Array, offset: Float32Array): boolean {
    if (!this.rig || !this.shells.length || !this.candidates.length) return false;
    // Bundle space -> rig space is the inverse of `v·lin + offset`.
    const inv = new Float64Array(16);
    const fwd = new Float64Array([
      lin[0], lin[3], lin[6], offset[0],
      lin[1], lin[4], lin[7], offset[1],
      lin[2], lin[5], lin[8], offset[2],
      0, 0, 0, 1,
    ]);
    if (!invertAffine(fwd, 0, inv, 0)) {
      console.warn(`[ogs] ${this.id}: sibling rig-space transform is singular — staying at neutral`);
      return false;
    }
    const n = this.neutralBundle.length;
    const rigSpace = new Float32Array(n);
    for (let i = 0; i < n; i += 3) {
      const x = this.neutralBundle[i], y = this.neutralBundle[i + 1], z = this.neutralBundle[i + 2];
      rigSpace[i]     = inv[0] * x + inv[1] * y + inv[2] * z + inv[3];
      rigSpace[i + 1] = inv[4] * x + inv[5] * y + inv[6] * z + inv[7];
      rigSpace[i + 2] = inv[8] * x + inv[9] * y + inv[10] * z + inv[11];
    }
    this.neutralRig = rigSpace;

    // Rest joint transforms, at the rig's own neutral controls.
    const d = this.rig.deformer;
    const { jointOut } = this.rig.evaluateRig(new Float32Array(this.controlNames.length));
    d.computeSkin(jointOut);
    const world = d.world as Float64Array;
    const J = d.J;
    const restInv = new Float64Array(J * 16);
    const origins = new Float32Array(J * 3);
    for (const j of this.candidates) {
      if (j < 0) continue;
      if (!invertAffine(world, j * 16, restInv, j * 16)) {
        console.warn(`[ogs] ${this.id}: joint ${j} rest transform is singular — its shell stays at neutral`);
      }
      origins[j * 3] = world[j * 16 + 3];
      origins[j * 3 + 1] = world[j * 16 + 7];
      origins[j * 3 + 2] = world[j * 16 + 11];
    }
    this.restInv = restInv;
    this.rel = new Float32Array(J * 16);
    this.scratch = new Float32Array(n);

    const centroids = shellCentroids(rigSpace, this.shells);
    this.jointOfShell = assignShellJoints(centroids, origins, this.candidates.filter((j) => j >= 0));
    const named = (j: number) => (j < 0 ? "(none)" : (this.rig!.manifest as { jointNames: string[] }).jointNames[j]);
    console.log(`[ogs] ${this.id}: ${this.shells.length} shells -> `
      + this.shells.map((s, k) => `${s.length}v:${named(this.jointOfShell[k])}`).join(", "));
    this.ready = this.jointOfShell.some((j) => j >= 0);
    if (!this.ready) console.warn(`[ogs] ${this.id}: no shell matched an eye/teeth joint — staying at neutral`);
    return this.ready;
  }

  /** True once a rig space has been adopted and at least one shell is bound. */
  get isDriving(): boolean {
    return this.ready;
  }

  private poseInto(rigVec: Float32Array, out: Float32Array): Float32Array {
    const neutral = this.neutralRig;
    if (!this.ready || !this.rig || !neutral || !this.restInv || !this.rel) {
      // Not bound: the branch's own neutral, which is what "frozen" means here.
      out.set(neutral ?? this.neutralBundle);
      return out;
    }
    const d = this.rig.deformer;
    const { jointOut } = this.rig.evaluateRig(rigVec);
    d.computeSkin(jointOut);
    const world = d.world as Float64Array;
    for (const j of this.jointOfShell) {
      if (j >= 0) mulAffine(world, j * 16, this.restInv, j * 16, this.rel, j * 16);
    }
    return poseShells(neutral, this.shells, this.jointOfShell, this.rel, out);
  }

  pose(rig: Float32Array): NodeTensor {
    if (!this.rig) throw new Error(`OrlShellDeform "${this.id}": init not run`);
    const out = this.scratch ?? (this.scratch = new Float32Array(this.neutralBundle.length));
    return { loc: "cpu", data: this.poseInto(rig, out), dims: this.dims } as NodeTensor;
  }

  async runCpu(rig: Float32Array): Promise<Float32Array> {
    if (!this.rig) throw new Error(`OrlShellDeform "${this.id}": init not run`);
    return this.poseInto(rig, new Float32Array(this.neutralBundle.length));
  }

  async download(t: NodeTensor): Promise<Float32Array> {
    if (t.loc === "cpu") return t.data as Float32Array;
    throw new Error(`OrlShellDeform "${this.id}": output is always on the CPU`);
  }

  async evaluate(
    _frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>> {
    const rig = inbound[this.rigPort];
    if (!rig) throw new Error(`OrlShellDeform "${this.id}": missing input "${this.rigPort}"`);
    if (rig.loc !== "cpu") throw new Error(`OrlShellDeform "${this.id}": rig must be on the CPU`);
    return { [this.outPort]: this.pose(rig.data as Float32Array) };
  }

  get activeSession(): null {
    return null;
  }

  dirtyOn(): readonly DirtySource[] {
    return ["rig"];
  }

  dispose(): void {
    // Shared with the head branch's OrlDeform — see createOrlRig's refcount.
    try { this.rig?.release(); } catch { /* best-effort */ }
    this.rig = null;
    this.neutralRig = null;
    this.restInv = null;
    this.rel = null;
    this.scratch = null;
    this.ready = false;
  }
}
